import { DatePipe, LowerCasePipe } from '@angular/common';
import { Component, computed, effect } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChatbotMultiComponent } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/chatbot-multi/chatbot-multi.component';
import { IKpiCard } from '@cta-analytics-details/cta-analytics-details-kpis-tile/cta-analytics-details-kpi-card/cta-analytics-details-kpi-card.interface';
import { AnalyticsDetailsTableComponent } from '@cta-analytics-dialog/deliverable-analytics-content/analytics-details-table/analytics-details-table.component';
import { DeliverableFindingsStore } from '@cta-analytics-dialog/deliverable-analytics-content/deliverable-findings.store';
import { LineGraphComponent } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.component';
import { ILineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.interface';
import { EDeliverableType } from '@enums/campaign.enum';
import { IChatBotMultiAnalytics } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { EmbeddedVideoPlayerComponent } from '@shared/components/embedded-video-player/embedded-video-player.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { AnalyticsDetailsUtilitiesService } from '@utilities/analytics-details-utilities.service';
import { StreamDetailsDeliverableBaseDirective } from '../../stream-details-deliverable-base.directive';
import { IChatbotMultiStreamAnalyticsTableData } from './chatbot-multi-stream-analytics.interface';

@Component({
  imports: [
    DatePipe,
    TranslateModule,
    LineGraphComponent,
    AnalyticsDetailsTableComponent,
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MatTooltipModule,
    EmbeddedVideoPlayerComponent,
    ChatbotMultiComponent,
    TextClampTooltipDirective,
    DotsSpinnerComponent,
    IconComponent,
    IconBadgeComponent,
    LowerCasePipe,
  ],
  providers: [DeliverableFindingsStore],
  selector: 'app-chatbot-multi-stream-analytics',
  templateUrl: './chatbot-multi-stream-analytics.component.html',
})
export class ChatbotMultiStreamAnalyticsComponent extends StreamDetailsDeliverableBaseDirective<
  IChatbotMultiStreamAnalyticsTableData,
  IChatBotMultiAnalytics
> {
  public override readonly kpiCardData: IKpiCard = {
    icon: 'view.svg',
    id: 'chatbot-multi-count',
    title: this.translate.instant('pages.campaignDetails.ctaAnalytics.details.chatBotMulti.totalCount'),
    value: 0,
  };
  public override readonly displayedColumns: string[] = ['timestamp', 'videoTime', 'user', 'title', 'actions'];
  public override readonly dataSource: MatTableDataSource<IChatbotMultiStreamAnalyticsTableData> =
    new MatTableDataSource();
  public override readonly deliverableType = EDeliverableType.chatbotMulti;

  public override readonly getKeywordCount = (keyword: string): number | null => {
    const deliverable = this.currentDeliverable();

    if (!deliverable) return null;

    const hasKeyword = deliverable.keywords?.some((k) => k.keyword === keyword);

    if (!hasKeyword) return null;

    const removedIds = this.manualFindingStatusStore?.allRemovedIds() ?? new Set<string>();

    return deliverable.findings?.filter((f) => !removedIds.has(f.id) && f.foundKeywords?.includes(keyword)).length ?? 0;
  };

  public override readonly currentDeliverable = computed(() => {
    const deliverableId = this.deliverableId();
    const executionAnalytics = this.executionAnalyticsWithFindings();

    if (deliverableId && executionAnalytics) {
      return executionAnalytics?.ctaAnalytics?.chatbotMultis?.find((cm) => cm.deliverableId === deliverableId);
    }

    return null;
  });

  constructor() {
    super();

    effect(() => {
      if (this.executionAnalyticsWithFindings()) {
        this.setStatus();
        this.setStreamData();
      }
    });

    effect(() => {
      this.manualFindingStatusStore?.serverRemovedIds();

      if (this.executionAnalyticsWithFindings()) {
        this.setCounts();

        if (this.timelineData()) {
          this.prepareChartAndTableData();
        }
      }
    });

    effect(() => {
      const deliverableId = this.deliverableId();
      const executionAnalytics = this.executionAnalyticsWithFindings();

      if (deliverableId && executionAnalytics) {
        const chatbotMulti = this.currentDeliverable();

        this.keywords = chatbotMulti?.keywords?.map((k) => k.keyword) ?? [];
      }
    });
  }

  protected override prepareChartAndTableData(): void {
    const tableData: IChatbotMultiStreamAnalyticsTableData[] = [];
    const dotGraphData: ILineGraphInputData[] = [];

    const currentAnalytics = this.currentDeliverable();
    const timelineData = this.timelineData() || [];
    const { sortedDictionary } = this.graphUtilities.processTimelineData(
      timelineData,
      this.stream()?.executionTime,
      this.stream()?.end,
    );
    const removedIds = this.manualFindingStatusStore?.serverRemovedIds() ?? new Set<string>();
    const keywordRowIds: Record<string, string[]> = {};

    currentAnalytics?.findings
      ?.filter((f) => !removedIds.has(f.id))
      .forEach((finding) => {
        const { id } = finding;
        const chatTimestamp = this.dateUtils.parseDateTimeFromApi(finding.timestamp).getTime();

        dotGraphData.push({
          id,
          status: finding.status,
          xAxisValue: chatTimestamp,
          yAxisValue: Math.round(this.graphUtilities.getInterpolatedViewCount(sortedDictionary, chatTimestamp)),
        });

        tableData.push({
          findingParentId: finding.parentId,
          foundKeywords: finding.foundKeywords ?? [],
          id,
          text: finding.text,
          timestamp: finding.timestamp,
          user: finding.user,
          videoTimeLabel: AnalyticsDetailsUtilitiesService.formatVideoTime(finding.videoTime),
          videoTimeSeconds: finding.videoTime ?? 0,
        });

        for (const kw of finding.foundKeywords ?? []) {
          if (!keywordRowIds[kw]) {
            keywordRowIds[kw] = [];
          }

          keywordRowIds[kw].push(id);
        }
      });

    this.dataSource.data = tableData;
    this.dataCount.set(tableData.length);
    this.dotGraphData.set([...dotGraphData].sort((a, b) => a.xAxisValue - b.xAxisValue));
    this.setKeywordTableRowIds(keywordRowIds);
  }

  protected override setCounts(): void {
    const removedIds = this.manualFindingStatusStore?.allRemovedIds() ?? new Set<string>();
    const activeFindings = this.currentDeliverable()?.findings?.filter((f) => !removedIds.has(f.id)) ?? [];

    this.kpiCardData.value = activeFindings.length;
  }

  protected override setKeywordList(): void {
    return;
  }
}
